import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, LayoutDashboard, BarChart3, Users, Shield, Settings, Mail, BookOpen, ClipboardList, Layers, Building2, Brain, Plus, ChevronRight, Key } from 'lucide-react';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { API } from '../lib/axiosClient';

// Alle navigierbaren Seiten mit Berechtigungsprüfung
const getAllPages = (t) => [
  { label: t('nav.dashboard'), path: '/home', icon: LayoutDashboard },
  { label: t('nav.newComplaint'), path: '/complaints/new', icon: Plus, permission: 'can_create_complaints' },
  { label: t('nav.allComplaints'), path: '/complaints-list', icon: FileText, permission: 'can_view_complaints' },
  { label: t('nav.myTasks'), path: '/my-tasks', icon: ClipboardList },
  { label: t('nav.actionsTracking'), path: '/actions-dashboard', icon: ClipboardList, permission: 'can_view_complaints' },
  { label: t('nav.emailInbox'), path: '/email-inbox', icon: Mail, permission: 'can_manage_users' },
  { label: t('nav.analyticsDashboard'), path: '/analytics', icon: BarChart3, permission: 'can_view_analytics' },
  { label: t('nav.userManagement'), path: '/user-management', icon: Users, permission: 'can_manage_users' },
  { label: t('nav.roleManagement'), path: '/role-management', icon: Key, permission: 'can_manage_users' },
  { label: t('nav.plantOrganization'), path: '/plant-matrix', icon: Building2, permission: 'can_manage_users' },
  { label: t('nav.zqmOrganization'), path: '/zqm-matrix', icon: Layers, permission: 'can_manage_users' },
  { label: t('nav.catalogManagement'), path: '/catalog-management', icon: BookOpen, permission: 'can_view_complaints' },
  { label: t('nav.emailLog'), path: '/email-log', icon: Mail, permission: 'can_manage_users' },
  { label: t('nav.auditLog'), path: '/audit-log', icon: Shield, permission: 'can_manage_users' },
  { label: t('nav.qualityGate'), path: '/quality-gate-settings', icon: Settings, permission: 'can_manage_users' },
  { label: t('nav.aiConfig'), path: '/ai-configuration', icon: Brain, permission: 'can_manage_users' },
];

export const CommandPalette = ({ isOpen, onClose }) => {
  const { user } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [complaints, setComplaints] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const ALL_PAGES = getAllPages(t);

  // Berechtigungsgefilterte Seiten
  const filteredPages = ALL_PAGES.filter(p => {
    if (!p.permission) return true;
    return user?.permissions?.[p.permission];
  });

  // Aktionen
  const actions = [
    { label: t('nav.newComplaintCreate'), icon: Plus, action: () => { navigate('/complaints/new'); onClose(); } },
  ];

  // Seiten filtern nach Query
  const matchedPages = query.length > 0
    ? filteredPages.filter(p => p.label.toLowerCase().includes(query.toLowerCase()))
    : filteredPages.slice(0, 6);

  // Aktionen filtern
  const matchedActions = query.length > 0
    ? actions.filter(a => a.label.toLowerCase().includes(query.toLowerCase()))
    : actions;

  // Reklamationen-Suche mit Debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) { setComplaints([]); return; }

    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get(`${API}/complaints`, {
          params: { search: query, limit: 5, skip: 0 },
          timeout: 5000,
        });
        setComplaints((res.data?.complaints || []).slice(0, 5));
      } catch {
        setComplaints([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // Alle Ergebnisse flach zusammenfuehren
  const allResults = [
    ...matchedPages.map(p => ({ type: 'page', ...p })),
    ...complaints.map(c => ({ type: 'complaint', label: `${c.complaint_number} — ${c.customer_name || ''}`, path: `/complaints/${c.id}/edit`, icon: FileText })),
    ...matchedActions.map(a => ({ type: 'action', ...a })),
  ];

  // Reset bei Oeffnen
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setComplaints([]);
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Keyboard Navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(prev => Math.min(prev + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && allResults[selectedIdx]) {
      e.preventDefault();
      const item = allResults[selectedIdx];
      if (item.action) {
        item.action();
      } else if (item.path) {
        navigate(item.path);
        onClose();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Query ändert → Index zurücksetzen
  useEffect(() => { setSelectedIdx(0); }, [query]);

  if (!isOpen) return null;

  const sectionLabel = (type) => {
    if (type === 'page') return 'Seiten';
    if (type === 'complaint') return t('nav.complaintsLabel');
    if (type === 'action') return 'Aktionen';
    return '';
  };

  let lastType = '';

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh]" data-testid="command-palette">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      {/* Modal */}
      <div className="relative w-full max-w-[560px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden" data-testid="command-palette-modal">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
          <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('common.searchPlaceholder')}
            className="flex-1 text-sm bg-transparent outline-none text-slate-900 placeholder:text-slate-400"
            data-testid="command-palette-input"
          />
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono text-slate-400 bg-slate-100 rounded border border-slate-200">ESC</kbd>
        </div>
        {/* Ergebnisse */}
        <div className="max-h-[360px] overflow-y-auto py-2">
          {searchLoading && (
            <div className="px-4 py-2 text-xs text-slate-400">Suche...</div>
          )}
          {allResults.length === 0 && query.length > 0 && !searchLoading && (
            <div className="px-4 py-6 text-center text-sm text-slate-400">{t('common.noResults')} "{query}"</div>
          )}
          {allResults.map((item, idx) => {
            const Icon = item.icon || FileText;
            const showSection = item.type !== lastType;
            lastType = item.type;
            return (
              <React.Fragment key={item.id || item.path || item.label || `cmd-${idx}`}>
                {showSection && (
                  <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{sectionLabel(item.type)}</div>
                )}
                <button
                  className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${
                    idx === selectedIdx
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  onClick={() => {
                    if (item.action) { item.action(); }
                    else if (item.path) { navigate(item.path); onClose(); }
                  }}
                  onMouseEnter={() => setSelectedIdx(idx)}
                  data-testid={`command-result-${idx}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0 opacity-60" />
                  <span className="flex-1 truncate">{item.label}</span>
                  <ChevronRight className="w-3.5 h-3.5 opacity-30" />
                </button>
              </React.Fragment>
            );
          })}
        </div>
        {/* Footer */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-slate-200 text-[10px] text-slate-400">
          <span>↑↓ Navigieren</span>
          <span>↵ Öffnen</span>
          <span>{t('nav.escClose')}</span>
        </div>
      </div>
    </div>
  );
};
